import { assertServiceHost, axiosClient } from '@nx-frontend/shared/api';
import {
  GetNotesResponse,
  NewNoteData,
  NoteAttachmentDto,
  NotePreviewDto,
  NoteDto,
  NotesPermissionsDto,
  NoteTextDto,
  ServicePayload,
} from '@nx-frontend/shared/types';

interface GetNotesPayload extends ServicePayload {
  workspaceId: string;
  query: string;
}
export const getNotes = async ({ host, userId, workspaceId, query }: GetNotesPayload): Promise<GetNotesResponse> => {
  const queryParams = query ? `?${query}` : '';
  const response = await axiosClient.get(
    `${assertServiceHost(host)}/v1/workspaces/${workspaceId}/notes${queryParams}`,
    { userId }
  );
  return {
    notes: response.data,
    count: response.headers['x-total-count'] ?? 0,
  };
};

interface GetNotesPermissions extends ServicePayload {
  orgId: string;
}
export const getNotesPermissions = async ({
  host,
  userId,
  orgId,
}: GetNotesPermissions): Promise<NotesPermissionsDto> => {
  if (!orgId) {
    return { notes: [], count: 0 };
  }
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/orgs/${orgId}/permissions`, { userId });
  return {
    notes: response.data,
    count: response.headers['x-total-count'] ?? 0,
  };
};

interface GetNotePayload extends ServicePayload {
  workspaceId: string;
  noteId: string;
}
export const getNote = async ({ host, userId, workspaceId, noteId }: GetNotePayload): Promise<NoteDto> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/notes/${noteId}`, {
    userId,
  });
  return response.data;
};

export const getNoteText = async ({ host, userId, workspaceId, noteId }: GetNotePayload): Promise<NoteTextDto> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/texts/${noteId}`, {
    userId,
  });
  return response.data;
};

export const getNotePreview = async ({
  host,
  userId,
  workspaceId,
  noteId,
}: GetNotePayload): Promise<NotePreviewDto> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/previews/${noteId}`, {
    userId,
  });
  return response.data;
};

export const getNoteAttachments = async ({
  host,
  userId,
  workspaceId,
  noteId,
}: GetNotePayload): Promise<NoteAttachmentDto[]> => {
  const response = await axiosClient.get(
    `${assertServiceHost(host)}/v1/workspaces/${workspaceId}/notes/${noteId}/attachments`,
    { userId }
  );
  return response.data;
};

interface CreateNotePayload extends ServicePayload {
  workspaceId: string;
  noteId: string;
  note: NewNoteData;
}
export const createNote = async ({ host, userId, workspaceId, noteId, note }: CreateNotePayload): Promise<NoteDto> => {
  const response = await axiosClient.post(
    `${assertServiceHost(host)}/v1/workspaces/${workspaceId}/notes/${noteId}?textVersion=${note.textVersion ?? 2}`,
    note,
    { userId }
  );
  return response.data;
};

interface UpdateNoteAttachmentPayload extends ServicePayload {
  workspaceId: string;
  attachmentId: string;
  displayName: string;
}
export const updateNoteAttachmentName = async ({
  host,
  userId,
  workspaceId,
  attachmentId,
  displayName,
}: UpdateNoteAttachmentPayload): Promise<NoteDto> => {
  const response = await axiosClient.post(
    `${assertServiceHost(host)}/v1/workspaces/${workspaceId}/attachments/${attachmentId}`,
    {
      displayName,
    },
    { userId }
  );
  return response.data;
};

interface CreateAttachmentPayload extends ServicePayload {
  workspaceId: string;
  attachmentId: string;
  noteGlobalId: string;
  source: {
    tempFileName?: string;
    tempStoredFileName?: string;
    attachmentGlobalId?: string;
    workspaceId?: string;
    externalUrl?: string;
    shareId?: string;
    shareSecurityKey?: string;
  };
  displayName?: string;
  mime?: string;
  inList: boolean;
  role: string;
  createSource: string;
  emitEvent?: boolean;
}
export const createNoteAttachment = async ({
  host,
  userId,
  workspaceId,
  attachmentId,
  noteGlobalId,
  source,
  displayName,
  mime,
  inList,
  role,
  createSource,
  emitEvent,
}: CreateAttachmentPayload): Promise<NoteAttachmentDto> => {
  const response = await axiosClient.put(
    `${assertServiceHost(host)}/v1/workspaces/${workspaceId}/attachments/${attachmentId}?isImport=false`,
    {
      noteGlobalId,
      source,
      displayName,
      mime,
      inList,
      role,
      createSource,
      emitEvent,
    },
    { userId }
  );
  return response.data;
};

/**
 * Fetches notes/folders from IS by their ids
 */
export async function getNotesByIds({
  host,
  workspaceId,
  notesIds,
  userId,
}: {
  host: string;
  workspaceId: string;
  notesIds: string[];
  userId: number;
}) {
  notesIds = notesIds.slice();
  const fetchBatch = async (ids: string[]) => {
    const filter = {
      globalId: ids,
    };
    const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/notes`, {
      params: {
        filter: JSON.stringify(filter),
      },
      headers: {
        Authorization: `Internal ${userId}:auto`,
      },
    });
    return response.data as NoteDto[];
  };

  // split notesIds into chunks of 100 and load them sequentially
  const chunks: string[][] = [];
  while (notesIds.length) {
    chunks.push(notesIds.splice(0, 100));
  }
  const result: NoteDto[] = [];
  for (const chunk of chunks) {
    const response = await fetchBatch(chunk);
    result.push(...response);
  }

  return result;
}
